export type StoredSortDirection = 'asc' | 'desc';

export interface StoredSort {
    key: string
    direction: StoredSortDirection
}

/**
 * Reads back a sort remembered under `storageKey`.
 *
 * A column that has since disappeared is dropped rather than sorting by a key nothing renders,
 * which `isKnownColumn` decides. Storage that is unavailable or holds something else reads as
 * "nothing remembered", so the caller falls back to its default order.
 */
export function readStoredSort(
    storageKey: string,
    isKnownColumn: (key: string) => boolean,
): StoredSort | null {
    try {
        const raw = localStorage.getItem(storageKey);
        if (!raw) {
            return null;
        }

        const parsed = JSON.parse(raw);
        if (
            parsed
            && typeof parsed.key === 'string'
            && isKnownColumn(parsed.key)
            && (parsed.direction === 'asc' || parsed.direction === 'desc')
        ) {
            return { key: parsed.key, direction: parsed.direction };
        }
    } catch {
        // Ignore unavailable/corrupt storage and fall back to the default order.
    }

    return null;
}

/** Remembers `sort` under `storageKey`; `null` forgets it. */
export function writeStoredSort(storageKey: string, sort: StoredSort | null): void {
    try {
        if (sort) {
            localStorage.setItem(storageKey, JSON.stringify({ key: sort.key, direction: sort.direction }));
        } else {
            localStorage.removeItem(storageKey);
        }
    } catch {
        // Ignore storage failures (quota, private mode, …); sorting still works in-memory.
    }
}
